import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, Lock, Mail, Home, Phone, ArrowLeft, Shield, MessageSquare } from "lucide-react";
import { API } from "@/App";

export const PortalLogin = () => {
  const navigate = useNavigate();
  
  // Login method: 'password' | 'email-otp' | 'sms-otp'
  const [loginMethod, setLoginMethod] = useState(null);
  
  // Form state
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  
  // Flow state
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpMethod, setOtpMethod] = useState(null); // 'email' or 'sms'

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/portal/auth/login`, {
        email,
        password
      });
      handleLoginSuccess(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestEmailOTP = async () => {
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/portal/auth/otp/request-email`, { email });
      toast.success(response.data.message);
      setOtpSent(true);
      setOtpMethod('email');
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error("Please wait before requesting another code");
      } else {
        toast.error(error.response?.data?.detail || "Failed to send code");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSMSOTP = async () => {
    if (!phone) {
      toast.error("Please enter your phone number");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/portal/auth/otp/request-sms`, { phone });
      toast.success(response.data.message);
      setOtpSent(true);
      setOtpMethod(response.data.method || 'sms');
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error("Please wait before requesting another code");
      } else {
        toast.error(error.response?.data?.detail || "Failed to send code");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const payload = { code: otpCode };
      if (loginMethod === 'email-otp') {
        payload.email = email;
      } else {
        payload.phone = phone;
      }

      const response = await axios.post(`${API}/portal/auth/otp/verify`, payload);
      handleLoginSuccess(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (data) => {
    const { access_token, client } = data;
    
    localStorage.setItem("portal_token", access_token);
    localStorage.setItem("portal_client", JSON.stringify(client));

    toast.success(`Welcome back, ${client.name}!`);

    switch (client.client_type) {
      case "seller":
        navigate("/portal/seller");
        break;
      case "buyer":
        navigate("/portal/buyer");
        break;
      case "landlord":
        navigate("/portal/landlord");
        break;
      case "tenant":
        navigate("/portal/tenant");
        break;
      default:
        navigate("/portal/seller");
    }
  };

  const resetForm = () => {
    setLoginMethod(null);
    setOtpSent(false);
    setOtpCode("");
    setOtpMethod(null);
  };

  // Method selection screen
  if (!loginMethod) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        <Card className="w-full max-w-md relative z-10 shadow-2xl border-slate-700 bg-white/95 backdrop-blur" data-testid="portal-login-card">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 w-16 h-16 bg-slate-800 rounded-xl flex items-center justify-center">
              <Home className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-800">Client Portal</CardTitle>
            <CardDescription className="text-slate-600">
              Goodacres Residential
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-4 space-y-3">
            <p className="text-sm text-slate-600 text-center mb-4">Choose how you'd like to sign in:</p>
            
            {/* Email Code Option */}
            <Button 
              variant="outline" 
              className="w-full h-14 justify-start gap-3 border-2 hover:border-blue-500 hover:bg-blue-50"
              onClick={() => setLoginMethod('email-otp')}
              data-testid="login-email-otp-btn"
            >
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-slate-800">Email Code</p>
                <p className="text-xs text-slate-500">Get a 6-digit code via email</p>
              </div>
            </Button>

            {/* SMS Code Option */}
            <Button 
              variant="outline" 
              className="w-full h-14 justify-start gap-3 border-2 hover:border-green-500 hover:bg-green-50"
              onClick={() => setLoginMethod('sms-otp')}
              data-testid="login-sms-otp-btn"
            >
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-green-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-slate-800">Text Message Code</p>
                <p className="text-xs text-slate-500">Get a 6-digit code via SMS</p>
              </div>
            </Button>

            {/* Password Option */}
            <Button 
              variant="outline" 
              className="w-full h-14 justify-start gap-3 border-2 hover:border-slate-500 hover:bg-slate-50"
              onClick={() => setLoginMethod('password')}
              data-testid="login-password-btn"
            >
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                <Lock className="w-5 h-5 text-slate-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-slate-800">Password</p>
                <p className="text-xs text-slate-500">Sign in with email & password</p>
              </div>
            </Button>

            {/* Security notice */}
            <div className="mt-6 p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 text-center flex items-center justify-center gap-1">
                <Shield className="w-3 h-3" />
                Secure login - Your information is protected
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Email OTP flow
  if (loginMethod === 'email-otp') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        <Card className="w-full max-w-md relative z-10 shadow-2xl border-slate-700 bg-white/95 backdrop-blur">
          <CardHeader className="text-center pb-2">
            <Button 
              variant="ghost" 
              size="sm" 
              className="absolute left-4 top-4"
              onClick={resetForm}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div className="mx-auto mb-4 w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-800">
              {otpSent ? "Enter Code" : "Email Login"}
            </CardTitle>
            <CardDescription className="text-slate-600">
              {otpSent 
                ? "We've sent a 6-digit code to your email" 
                : "We'll send you a secure login code"}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-4">
            {!otpSent ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      data-testid="otp-email-input"
                    />
                  </div>
                </div>
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={handleRequestEmailOTP}
                  disabled={loading}
                  data-testid="send-email-code-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    "Send Login Code"
                  )}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="otp" className="text-slate-700">6-Digit Code</Label>
                  <Input
                    id="otp"
                    type="text"
                    placeholder="000000"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center text-2xl tracking-widest font-mono"
                    maxLength={6}
                    data-testid="otp-code-input"
                  />
                  <p className="text-xs text-slate-500 text-center">Code expires in 5 minutes</p>
                </div>
                <Button 
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={loading || otpCode.length !== 6}
                  data-testid="verify-otp-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Verifying...
                    </>
                  ) : (
                    "Verify & Sign In"
                  )}
                </Button>
                <Button 
                  type="button"
                  variant="ghost"
                  className="w-full text-slate-600"
                  onClick={() => { setOtpSent(false); setOtpCode(""); }}
                >
                  Didn't receive code? Send again
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // SMS OTP flow
  if (loginMethod === 'sms-otp') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        <Card className="w-full max-w-md relative z-10 shadow-2xl border-slate-700 bg-white/95 backdrop-blur">
          <CardHeader className="text-center pb-2">
            <Button 
              variant="ghost" 
              size="sm" 
              className="absolute left-4 top-4"
              onClick={resetForm}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center">
              <MessageSquare className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-800">
              {otpSent ? "Enter Code" : "Text Message Login"}
            </CardTitle>
            <CardDescription className="text-slate-600">
              {otpSent 
                ? `We've sent a 6-digit code to your ${otpMethod === 'email' ? 'email' : 'phone'}`
                : "We'll send you a secure login code"}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-4">
            {!otpSent ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-700">Phone Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="07700 900000"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="pl-10"
                      data-testid="otp-phone-input"
                    />
                  </div>
                </div>
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={handleRequestSMSOTP}
                  disabled={loading}
                  data-testid="send-sms-code-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    "Send Login Code"
                  )}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="otp" className="text-slate-700">6-Digit Code</Label>
                  <Input
                    id="otp"
                    type="text"
                    placeholder="000000"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center text-2xl tracking-widest font-mono"
                    maxLength={6}
                    data-testid="otp-code-input"
                  />
                  <p className="text-xs text-slate-500 text-center">Code expires in 5 minutes</p>
                </div>
                <Button 
                  type="submit"
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={loading || otpCode.length !== 6}
                  data-testid="verify-otp-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Verifying...
                    </>
                  ) : (
                    "Verify & Sign In"
                  )}
                </Button>
                <Button 
                  type="button"
                  variant="ghost"
                  className="w-full text-slate-600"
                  onClick={() => { setOtpSent(false); setOtpCode(""); }}
                >
                  Didn't receive code? Send again
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Password login flow
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <Card className="w-full max-w-md relative z-10 shadow-2xl border-slate-700 bg-white/95 backdrop-blur" data-testid="portal-login-card">
        <CardHeader className="text-center pb-2">
          <Button 
            variant="ghost" 
            size="sm" 
            className="absolute left-4 top-4"
            onClick={resetForm}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div className="mx-auto mb-4 w-16 h-16 bg-slate-800 rounded-xl flex items-center justify-center">
            <Home className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">Sign In</CardTitle>
          <CardDescription className="text-slate-600">
            Goodacres Residential
          </CardDescription>
        </CardHeader>
        
        <CardContent className="pt-4">
          <form onSubmit={handlePasswordLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  data-testid="portal-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  data-testid="portal-password-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-slate-800 hover:bg-slate-700"
              disabled={loading}
              data-testid="portal-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <div className="mt-6 p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-500 text-center">
              <Lock className="w-3 h-3 inline mr-1" />
              Secure login - Your information is protected
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalLogin;
